// INSERT column + cell planners. Kept out of applyStructuralEdit.dml.ts (which
// owns the row/assignment add/remove) because these ops are multi-range: an
// `add`/`remove-insert-column` touches the column list AND one cell per VALUES
// row, and the cells have no stored ranges — they are re-split from each row's
// range at plan time via findTopLevelCommas (the same top-level-comma re-split
// the comma-list planners use), so the module needs its own scanning helpers.
//
// PRD: Ralph/SemanticResolverEdit/PRD.md § "INSERT editing completion" (§2.4 S6).
//
// - add-insert-column: append `, <columnText>` to the column list + `, <value>`
//   (default NULL) before each row's closing paren. VALUES-form + explicit
//   column list only; every row gets one cell — shape stays consistent.
// - remove-insert-column: drop the column (+ its separator) and the matching
//   cell (+ separator) from EVERY row. All-or-nothing: if any row's cell count
//   disagrees with the column count, or only one column remains, plan nothing.
// - modify-insert-values-cell: byte-precise replace of a single cell.

import type { SqlRange } from '../../sql-grammar/model';
import { rangeFromOffsets } from '../../sql-grammar/document/lineIndex';
import { findTopLevelCommas } from '../structure/scanner';
import type { PlannedByteEdit, StructuralEdit } from '../types';

import type { EditCtx } from './applyStructuralEdit';
import { planListInsert, planListRemove } from './applyStructuralEdit.lists';
import {
  replaceEdit, trimLeadingWs, trimTrailingWs, type ListItem,
} from './applyStructuralEdit.shared';

// Re-split a VALUES row into per-cell ListItems. The row range starts at the
// opening `(` and ends ONE PAST the closing `)` (parseValuesRows math), so the
// inner span is [start+1, end-1). Cells are the top-level-comma slices, each
// trimmed of surrounding whitespace so the returned ranges hug the cell text.
function rowCellItems(ctx: EditCtx, rowRange: SqlRange): ListItem[] {
  const { source, options, lineIdx } = ctx;
  const innerStart = rowRange.startOffset + 1;
  const innerEnd = rowRange.endOffset - 1;
  if (innerEnd < innerStart) return [];
  const commas = findTopLevelCommas(source, innerStart, innerEnd, options);
  const rawStarts = [innerStart, ...commas.map((c) => c + 1)];
  const rawEnds = [...commas, innerEnd];
  return rawStarts.map((s, k) => {
    const lo = trimLeadingWs(source, s, rawEnds[k]);
    const hi = trimTrailingWs(source, lo, rawEnds[k]);
    return { range: rangeFromOffsets(lineIdx, lo, hi) };
  });
}

function planAddInsertColumn(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'add-insert-column' }>,
): readonly PlannedByteEdit[] {
  const cols = ctx.structure.insertColumns;
  const rowRanges = ctx.structure.insertValueRowRanges;
  // VALUES-form + explicit column list only (decision 4). Implicit column list
  // / INSERT…SELECT / DEFAULT VALUES all lack one of these ⇒ no edits.
  if (!cols || cols.length === 0) return [];
  if (!rowRanges || rowRanges.length === 0) return [];

  const valueText = edit.valueText ?? 'NULL';
  const colItems: ListItem[] = cols.map((c) => ({ range: c.range }));
  const edits: PlannedByteEdit[] = [
    planListInsert(ctx, colItems, edit.columnText, undefined, 'add-insert-column'),
  ];
  for (const rowRange of rowRanges) {
    const cells = rowCellItems(ctx, rowRange);
    if (cells.length === 0) return []; // degenerate `()` row — bail, all-or-nothing
    edits.push(planListInsert(ctx, cells, valueText, undefined, 'add-insert-column:cell'));
  }
  return edits;
}

function planRemoveInsertColumn(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'remove-insert-column' }>,
): readonly PlannedByteEdit[] {
  const cols = ctx.structure.insertColumns;
  const rowRanges = ctx.structure.insertValueRowRanges;
  if (!cols || cols.length === 0) return [];
  if (!rowRanges || rowRanges.length === 0) return [];
  // Removing the last remaining column would emit `INSERT INTO t () VALUES ()`
  // (decision 5). Refuse — defense in depth behind the disabled UI control.
  if (cols.length === 1) return [];
  if (edit.index < 0 || edit.index >= cols.length) return [];

  // Strict shape guard: every row must have exactly one cell per column, or we
  // plan NOTHING (no partial row mutations — decision 5).
  const perRowCells = rowRanges.map((r) => rowCellItems(ctx, r));
  if (perRowCells.some((cells) => cells.length !== cols.length)) return [];

  const colItems: ListItem[] = cols.map((c) => ({ range: c.range }));
  const colEdit = planListRemove(ctx, colItems, edit.index, 'remove-insert-column');
  if (!colEdit) return [];
  const edits: PlannedByteEdit[] = [colEdit];
  for (const cells of perRowCells) {
    const cellEdit = planListRemove(ctx, cells, edit.index, 'remove-insert-column:cell');
    if (!cellEdit) return []; // unreachable given the shape guard, but keep it total
    edits.push(cellEdit);
  }
  return edits;
}

export function planInsertColumnEdit(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'add-insert-column' | 'remove-insert-column' }>,
): readonly PlannedByteEdit[] {
  return edit.op === 'add-insert-column'
    ? planAddInsertColumn(ctx, edit)
    : planRemoveInsertColumn(ctx, edit);
}

export function planModifyInsertValuesCell(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'modify-insert-values-cell' }>,
): readonly PlannedByteEdit[] {
  const rowRanges = ctx.structure.insertValueRowRanges;
  if (!rowRanges || rowRanges.length === 0) return [];
  if (edit.rowIndex < 0 || edit.rowIndex >= rowRanges.length) return [];
  const cells = rowCellItems(ctx, rowRanges[edit.rowIndex]);
  if (edit.cellIndex < 0 || edit.cellIndex >= cells.length) return [];
  const target = cells[edit.cellIndex].range;
  return [replaceEdit(ctx, target.startOffset, target.endOffset, edit.newText, 'modify-insert-values-cell')];
}
